// ─── Council Agent SVG Icons ────────────────────────────────────────────────
// Dark navy circular background, white line-art symbol, colored accent arc.

interface IconProps {
  className?: string
  size?: number
}

const STROKE_PROPS = {
  stroke: '#e2e8f0',
  strokeWidth: 1.5,
  fill: 'none',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

function IconShell({
  children,
  accent,
  className,
  size = 64,
}: {
  children: React.ReactNode
  accent: string
  className?: string
  size?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="50" cy="50" r="48" fill="#0a0f1e" stroke="#1e293b" strokeWidth="1" />
      {children}
      <path d="M 15 92 A 48 48 0 0 0 85 92" stroke={accent} strokeWidth="2.5" fill="none" />
    </svg>
  )
}

// ─── ATHENA — Owl face ──────────────────────────────────────────────────────

export function AthenaIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#00d4ff" className={className} size={size}>
      {/* Left eye */}
      <circle cx="38" cy="42" r="8" {...STROKE_PROPS} />
      {/* Right eye */}
      <circle cx="62" cy="42" r="8" {...STROKE_PROPS} />
      {/* Beak */}
      <path d="M 46 50 L 50 58 L 54 50" {...STROKE_PROPS} />
      {/* Left ear tuft */}
      <path d="M 30 34 L 26 22 L 34 30" {...STROKE_PROPS} />
      {/* Right ear tuft */}
      <path d="M 70 34 L 74 22 L 66 30" {...STROKE_PROPS} />
      {/* Body lines */}
      <path d="M 38 50 L 34 68 L 50 72 L 66 68 L 62 50" {...STROKE_PROPS} />
      <path d="M 42 58 L 50 60 L 58 58" {...STROKE_PROPS} />
      <path d="M 40 64 L 50 66 L 60 64" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── SAGA — Open book ───────────────────────────────────────────────────────

export function ClioIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#a855f7" className={className} size={size}>
      {/* Book outline */}
      <path d="M 25 30 L 25 72 L 50 68 L 75 72 L 75 30 L 50 34 L 25 30 Z" {...STROKE_PROPS} />
      {/* Centre spine */}
      <line x1="50" y1="34" x2="50" y2="68" {...STROKE_PROPS} />
      {/* Left page lines */}
      <line x1="30" y1="40" x2="46" y2="42" {...STROKE_PROPS} />
      <line x1="30" y1="47" x2="46" y2="49" {...STROKE_PROPS} />
      <line x1="30" y1="54" x2="46" y2="56" {...STROKE_PROPS} />
      <line x1="30" y1="61" x2="46" y2="63" {...STROKE_PROPS} />
      {/* Right page lines */}
      <line x1="54" y1="42" x2="70" y2="40" {...STROKE_PROPS} />
      <line x1="54" y1="49" x2="70" y2="47" {...STROKE_PROPS} />
      <line x1="54" y1="56" x2="70" y2="54" {...STROKE_PROPS} />
      <line x1="54" y1="63" x2="70" y2="61" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── ARTEMIS — Crescent moon with arrow ─────────────────────────────────────

export function ArtemisIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#22c55e" className={className} size={size}>
      {/* Crescent moon — outer arc then inner arc offset to the right */}
      <path
        d="M 44 28 A 22 22 0 1 0 44 72 A 16 16 0 1 1 44 28 Z"
        {...STROKE_PROPS}
      />
      {/* Arrow crossing diagonally */}
      <line x1="38" y1="62" x2="68" y2="28" {...STROKE_PROPS} />
      {/* Arrowhead */}
      <path d="M 62 28 L 68 28 L 68 34" {...STROKE_PROPS} />
      {/* Arrow fletching */}
      <line x1="38" y1="62" x2="42" y2="56" {...STROKE_PROPS} />
      <line x1="38" y1="62" x2="44" y2="62" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── ISIS — Ankh with wings ─────────────────────────────────────────────────

export function HeraIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#eab308" className={className} size={size}>
      {/* Ankh loop */}
      <circle cx="50" cy="34" r="9" {...STROKE_PROPS} />
      {/* Ankh vertical bar */}
      <line x1="50" y1="43" x2="50" y2="74" {...STROKE_PROPS} />
      {/* Ankh horizontal bar */}
      <line x1="38" y1="54" x2="62" y2="54" {...STROKE_PROPS} />
      {/* Left wing */}
      <path d="M 38 54 Q 28 48 20 52 Q 26 44 38 46" {...STROKE_PROPS} />
      <path d="M 38 54 Q 30 56 24 60 Q 30 52 38 50" {...STROKE_PROPS} />
      {/* Right wing */}
      <path d="M 62 54 Q 72 48 80 52 Q 74 44 62 46" {...STROKE_PROPS} />
      <path d="M 62 54 Q 70 56 76 60 Q 70 52 62 50" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── PLUTUS — Single leaf ───────────────────────────────────────────────────

export function PlutusIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#86efac" className={className} size={size}>
      {/* Leaf outline */}
      <path
        d="M 50 24 Q 72 38 72 55 Q 72 72 50 78 Q 28 72 28 55 Q 28 38 50 24 Z"
        {...STROKE_PROPS}
      />
      {/* Central vein */}
      <line x1="50" y1="28" x2="50" y2="78" {...STROKE_PROPS} />
      {/* Left branch veins */}
      <line x1="50" y1="40" x2="36" y2="36" {...STROKE_PROPS} />
      <line x1="50" y1="48" x2="34" y2="46" {...STROKE_PROPS} />
      <line x1="50" y1="56" x2="35" y2="56" {...STROKE_PROPS} />
      <line x1="50" y1="64" x2="38" y2="66" {...STROKE_PROPS} />
      {/* Right branch veins */}
      <line x1="50" y1="40" x2="64" y2="36" {...STROKE_PROPS} />
      <line x1="50" y1="48" x2="66" y2="46" {...STROKE_PROPS} />
      <line x1="50" y1="56" x2="65" y2="56" {...STROKE_PROPS} />
      <line x1="50" y1="64" x2="62" y2="66" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── MNEMOSYNE — Geometric raven ────────────────────────────────────────────

export function MnemosyneIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#3b82f6" className={className} size={size}>
      {/* Body — angular polygon facing left */}
      <polygon
        points="30,50 38,36 55,32 65,38 68,50 62,62 48,68 35,64"
        {...STROKE_PROPS}
      />
      {/* Eye */}
      <circle cx="44" cy="42" r="2.5" {...STROKE_PROPS} />
      {/* Beak */}
      <path d="M 30 50 L 22 48 L 30 44" {...STROKE_PROPS} />
      {/* Wing suggestion */}
      <path d="M 55 38 L 72 30 L 76 42 L 68 50" {...STROKE_PROPS} />
      <line x1="60" y1="36" x2="68" y2="34" {...STROKE_PROPS} />
      {/* Tail */}
      <path d="M 48 68 L 44 78 L 52 76 L 56 72" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── ANUBIS — Scales of justice ─────────────────────────────────────────────

export function AsclepiusIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#ec4899" className={className} size={size}>
      {/* Vertical pole */}
      <line x1="50" y1="26" x2="50" y2="76" {...STROKE_PROPS} />
      {/* Base */}
      <line x1="40" y1="76" x2="60" y2="76" {...STROKE_PROPS} />
      {/* Balance beam */}
      <line x1="24" y1="38" x2="76" y2="38" {...STROKE_PROPS} />
      {/* Fulcrum triangle */}
      <path d="M 46 32 L 50 26 L 54 32" {...STROKE_PROPS} />
      {/* Left chain */}
      <line x1="28" y1="38" x2="28" y2="52" {...STROKE_PROPS} />
      {/* Left pan */}
      <path d="M 20 52 Q 28 58 36 52" {...STROKE_PROPS} />
      {/* Right chain */}
      <line x1="72" y1="38" x2="72" y2="52" {...STROKE_PROPS} />
      {/* Right pan */}
      <path d="M 64 52 Q 72 58 80 52" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── BIFROST — Concentric arcs ──────────────────────────────────────────────

export function AetherIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#10b981" className={className} size={size}>
      {/* Four concentric arcs radiating upward from bottom centre */}
      <path d="M 26 78 A 24 24 0 0 1 74 78" {...STROKE_PROPS} />
      <path d="M 30 78 A 20 20 0 0 1 70 78" {...STROKE_PROPS} />
      <path d="M 34 78 A 16 16 0 0 1 66 78" {...STROKE_PROPS} />
      <path d="M 38 78 A 12 12 0 0 1 62 78" {...STROKE_PROPS} />
      {/* Vertical centre marker */}
      <line x1="50" y1="54" x2="50" y2="38" {...STROKE_PROPS} />
      <circle cx="50" cy="34" r="3" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── KRATOS — Hammer (Mjolnir) with lightning ───────────────────────────────

export function KratosIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#f59e0b" className={className} size={size}>
      {/* Hammer head — rectangle */}
      <rect x="30" y="30" width="30" height="18" rx="2" {...STROKE_PROPS} />
      {/* Handle */}
      <line x1="45" y1="48" x2="45" y2="74" {...STROKE_PROPS} />
      {/* Handle grip */}
      <line x1="40" y1="72" x2="50" y2="72" {...STROKE_PROPS} />
      <line x1="40" y1="74" x2="50" y2="74" {...STROKE_PROPS} />
      {/* Lightning bolt */}
      <path d="M 68 28 L 64 42 L 70 42 L 66 56" {...STROKE_PROPS} />
      {/* Spark marks */}
      <line x1="72" y1="34" x2="76" y2="32" {...STROKE_PROPS} />
      <line x1="74" y1="44" x2="78" y2="44" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── PROMETHEUS — Torch with flame ──────────────────────────────────────────

export function PrometheusIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#8b5cf6" className={className} size={size}>
      {/* Torch handle */}
      <rect x="46" y="52" width="8" height="24" rx="2" {...STROKE_PROPS} />
      {/* Handle band */}
      <line x1="44" y1="56" x2="56" y2="56" {...STROKE_PROPS} />
      {/* Flame — three overlapping teardrop curves */}
      <path
        d="M 50 52 Q 42 42 44 34 Q 46 26 50 22 Q 54 26 56 34 Q 58 42 50 52 Z"
        {...STROKE_PROPS}
      />
      <path
        d="M 50 52 Q 46 44 48 36 Q 49 30 50 28 Q 51 30 52 36 Q 54 44 50 52 Z"
        {...STROKE_PROPS}
      />
      <path
        d="M 50 48 Q 47 42 48 38 Q 50 34 52 38 Q 53 42 50 48 Z"
        {...STROKE_PROPS}
      />
    </IconShell>
  )
}

// ─── APOLLO — Lyre ──────────────────────────────────────────────────────────

export function ApolloIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#fcd34d" className={className} size={size}>
      {/* Two curved arms of the lyre */}
      <path d="M 38 70 Q 26 56 32 32" {...STROKE_PROPS} />
      <path d="M 62 70 Q 74 56 68 32" {...STROKE_PROPS} />
      {/* Crossbar at the top */}
      <line x1="32" y1="32" x2="68" y2="32" {...STROKE_PROPS} />
      {/* Soundbox base */}
      <path d="M 38 70 L 62 70" {...STROKE_PROPS} />
      {/* Strings */}
      <line x1="44" y1="34" x2="44" y2="68" {...STROKE_PROPS} />
      <line x1="50" y1="34" x2="50" y2="69" {...STROKE_PROPS} />
      <line x1="56" y1="34" x2="56" y2="68" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── HELIOS — Sun ───────────────────────────────────────────────────────────

export function HeliosIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#f97316" className={className} size={size}>
      {/* Solar disc */}
      <circle cx="50" cy="50" r="15" {...STROKE_PROPS} />
      {/* Eight rays */}
      <line x1="50" y1="20" x2="50" y2="28" {...STROKE_PROPS} />
      <line x1="50" y1="72" x2="50" y2="80" {...STROKE_PROPS} />
      <line x1="20" y1="50" x2="28" y2="50" {...STROKE_PROPS} />
      <line x1="72" y1="50" x2="80" y2="50" {...STROKE_PROPS} />
      <line x1="29" y1="29" x2="35" y2="35" {...STROKE_PROPS} />
      <line x1="65" y1="65" x2="71" y2="71" {...STROKE_PROPS} />
      <line x1="71" y1="29" x2="65" y2="35" {...STROKE_PROPS} />
      <line x1="35" y1="65" x2="29" y2="71" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── CALLIOPE — Megaphone (Communications & PR) ─────────────────────────────

export function CalliopeIcon({ className, size }: IconProps) {
  return (
    <IconShell accent="#e879f9" className={className} size={size}>
      {/* Megaphone horn */}
      <path d="M 30 42 L 58 32 L 58 68 L 30 58 Z" {...STROKE_PROPS} />
      {/* Mouthpiece */}
      <path d="M 58 40 L 66 44 L 66 56 L 58 60" {...STROKE_PROPS} />
      {/* Handle */}
      <path d="M 36 58 L 36 70" {...STROKE_PROPS} />
      {/* Sound waves */}
      <path d="M 72 40 A 12 12 0 0 1 72 60" {...STROKE_PROPS} />
      <path d="M 76 34 A 20 20 0 0 1 76 66" {...STROKE_PROPS} />
    </IconShell>
  )
}

// ─── Lookup helper ──────────────────────────────────────────────────────────

// Keyed by skill_name (the `name` field in the API response)
const ICON_MAP: Record<string, React.ComponentType<IconProps>> = {
  'guardian-coo': AthenaIcon,
  // Legacy alias — pre-DEC-021 activity rows wrote skill_name='chief-of-staff'.
  'chief-of-staff': AthenaIcon,
  'content-engine': ClioIcon,
  'sales-pipeline': ArtemisIcon,
  'customer-success': HeraIcon,
  'finance-ops': PlutusIcon,
  'market-intel': MnemosyneIcon,
  'dev-ops': AsclepiusIcon,
  partnerships: AetherIcon,
  'security-infra': KratosIcon,
  'product-intel': PrometheusIcon,
  'content-marketing': ApolloIcon,
  'social-media': HeliosIcon,
  'communications-pr': CalliopeIcon,
}

// Fallback: match by display name (case-insensitive)
const DISPLAY_NAME_MAP: Record<string, React.ComponentType<IconProps>> = {
  athena: AthenaIcon,
  clio: ClioIcon,
  artemis: ArtemisIcon,
  hera: HeraIcon,
  plutus: PlutusIcon,
  mnemosyne: MnemosyneIcon,
  asclepius: AsclepiusIcon,
  aether: AetherIcon,
  kratos: KratosIcon,
  prometheus: PrometheusIcon,
  apollo: ApolloIcon,
  helios: HeliosIcon,
  calliope: CalliopeIcon,
}

/**
 * Returns the SVG icon component for a given agent skill name or display name.
 * Tries skill_name first (e.g. "guardian-coo"), then display name (e.g. "athena").
 * Returns `undefined` if no matching icon exists.
 */
export function getAgentIcon(agentName: string | null | undefined): React.ComponentType<IconProps> | undefined {
  if (!agentName) return undefined
  return ICON_MAP[agentName.toLowerCase()] ?? DISPLAY_NAME_MAP[agentName.toLowerCase()]
}
